import React, { useEffect, useState, useMemo } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import SupportStyleTable from "../../../Components/Table/SupportStyleTable";
import { fetchComplaints } from "../../../Redux/Slice/Homepage";
import "../../CustomerReq/InhouseRnd.css";
import {
  FileText,
  Building2,
  FolderGit2,
  User,
  Calendar,
  Clock,
  Eye,
  Filter,
} from "lucide-react";

const HomeShowAllCusReq = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { complaintsData } = useSelector((state) => state.Home);

  const [filters, setFilters] = useState({
    tokenName: "",
    companyName: "",
    project: "",
    supportTeam: "",
    status: "",
    startDate: "",
    endDate: "",
  });

  const [showTodayOnly, setShowTodayOnly] = useState(false);

  useEffect(() => {
    dispatch(fetchComplaints());
  }, [dispatch]);

  // Calculate duration
  const calculateTerm = (startDate, endDate, status) => {
    if (status?.toLowerCase() !== "completed") return "-";
    if (!startDate || !endDate) return "-";

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (isNaN(start) || isNaN(end) || end < start) return "-";

    const diffMs = end - start;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHrs = Math.floor((diffMs / (1000 * 60 * 60)) % 24);
    const diffMins = Math.floor((diffMs / (1000 * 60)) % 60);
    const diffSecs = Math.floor((diffMs / 1000) % 60);

    let parts = [];
    if (diffDays > 0) {
      parts.push(`${diffDays}d`);
      if (diffHrs > 0) parts.push(`${diffHrs}h`);
    } else if (diffHrs > 0) {
      parts.push(`${diffHrs}h`);
      if (diffMins > 0 || parts.length === 0) parts.push(`${diffMins}m`);
    } else {
      if (diffMins > 0) parts.push(`${diffMins}m`);
      if (diffSecs > 0 || parts.length === 0) parts.push(`${diffSecs}s`);
    }

    return parts.join(" ");
  };

  const companyNameOptions = useMemo(() => {
    return [
      ...new Set(complaintsData?.data?.map((el) => el.CompanyName?.WhaClientName)),
    ].filter(Boolean);
  }, [complaintsData]);

  const projectOptions = useMemo(() => {
    return [
      ...new Set(complaintsData?.data?.map((el) => el.Project?.project_name)),
    ].filter(Boolean);
  }, [complaintsData]);

  const supportTeamOptions = useMemo(() => {
    return [
      ...new Set(complaintsData?.data?.map((el) => el.SuportTeamId?.Name)),
    ].filter(Boolean);
  }, [complaintsData]);

  const statusOptions = useMemo(() => {
    return [
      ...new Set(complaintsData?.data?.map((el) => el.Status)),
    ].filter(Boolean);
  }, [complaintsData]);

  const isDateInRange = (isoDate, startDate, endDate) => {
    const date = new Date(isoDate);
    if (showTodayOnly) {
      const today = new Date();
      return (
        date.getFullYear() === today.getFullYear() &&
        date.getMonth() === today.getMonth() &&
        date.getDate() === today.getDate()
      );
    } else if (startDate && endDate) {
      return date >= new Date(startDate) && date <= new Date(endDate);
    }
    return true;
  };

  const formatDate = (isoDate) => {
    if (!isoDate) return "-";
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return "-";
    return `${date.getDate().toString().padStart(2, "0")}/${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}/${date.getFullYear()}`;
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const filteredRows = useMemo(() => {
    return (
      complaintsData?.data
        ?.filter((el) => {
          const {
            tokenName,
            companyName,
            project,
            supportTeam,
            status,
            startDate,
            endDate,
          } = filters;

          const matchesToken =
            !tokenName ||
            el.CallNo?.toLowerCase().includes(tokenName.toLowerCase());
          const matchesCompany =
            !companyName || el.CompanyName?.WhaClientName === companyName;
          const matchesProject =
            !project || el.Project?.project_name === project;
          const matchesTeam =
            !supportTeam || el.SuportTeamId?.Name === supportTeam;
          const matchesStatus = !status || el.Status === status;
          const matchesDate = isDateInRange(el.Date, startDate, endDate);

          return (
            matchesToken &&
            matchesCompany &&
            matchesProject &&
            matchesTeam &&
            matchesStatus &&
            matchesDate
          );
        })
        ?.map((el, index) => ({
          id: index + 1,
          MainId: el._id,
          tokenName: el.CallNo || "-",
          companyName: el.CompanyName?.WhaClientName || "-",
          project: el.Project?.project_name || "-",
          supportTeam: el.SuportTeamId?.Name || "-",
          status: el.Status || "Pending",
          date: formatDate(el.Date),
          term: calculateTerm(el.Date, el.Complate_Date, el.Status),
        })) || []
    );
  }, [complaintsData, filters, showTodayOnly]);

  const handleRowClick = (row) => {
    navigate(`/ShowAllTask/CustomerrequustDetail/${row.MainId}`);
  };

  // Reusable cell renderers
  const renderTokenCell = (params) => (
    <span className="inline-flex items-center justify-center font-bold text-[#133A69] bg-blue-50 border border-blue-200 px-2 py-0.5 rounded text-[11px]">
      #{params.value}
    </span>
  );

  const renderCompanyCell = (params) => (
    <div className="flex items-center gap-1.5 min-w-0">
      <Building2 size={13} className="text-slate-400 shrink-0" />
      <span className="font-semibold text-slate-800 truncate" title={params.value}>
        {params.value}
      </span>
    </div>
  );

  const renderProjectCell = (params) => (
    <div className="flex items-center gap-1.5 min-w-0">
      <FolderGit2 size={12} className="text-slate-400 shrink-0" />
      <span className="text-slate-700 font-medium truncate" title={params.value}>
        {params.value}
      </span>
    </div>
  );

  const renderTeamCell = (params) => (
    <div className="flex items-center gap-1.5 min-w-0">
      <User size={12} className="text-slate-400 shrink-0" />
      <span className="text-slate-700 font-medium truncate" title={params.value}>
        {params.value}
      </span>
    </div>
  );

  const renderStatusCell = (params) => {
    const val = String(params.value || "").toLowerCase();
    let badgeClass = "bg-slate-100 text-slate-700 border-slate-200";
    if (val === "completed") {
      badgeClass = "bg-emerald-50 text-emerald-700 border-emerald-200";
    } else if (val === "running" || val === "accepted") {
      badgeClass = "bg-blue-50 text-blue-700 border-blue-200";
    } else if (val === "holding") {
      badgeClass = "bg-indigo-50 text-indigo-700 border-indigo-200";
    } else if (val === "pending") {
      badgeClass = "bg-amber-50 text-amber-700 border-amber-200";
    }

    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold border ${badgeClass}`}>
        {params.value}
      </span>
    );
  };

  const renderDateCell = (params) => (
    <div className="inline-flex items-center gap-1 text-slate-600 text-xs font-medium">
      <Calendar size={12} className="text-slate-400 shrink-0" />
      <span>{params.value}</span>
    </div>
  );

  const renderTermCell = (params) => (
    <div className="inline-flex items-center gap-1 text-slate-600 text-xs font-medium">
      <Clock size={12} className="text-slate-400 shrink-0" />
      <span>{params.value}</span>
    </div>
  );

  const columns = [
    { field: "tokenName", headerName: "CALL NO.", width: 100, minWidth: 90, renderCell: renderTokenCell },
    { field: "companyName", headerName: "COMPANY NAME", minWidth: 220, renderCell: renderCompanyCell },
    { field: "project", headerName: "PROJECT", minWidth: 160, renderCell: renderProjectCell },
    { field: "supportTeam", headerName: "SUPPORT TEAM", minWidth: 160, renderCell: renderTeamCell },
    { field: "status", headerName: "STATUS", width: 115, minWidth: 100, renderCell: renderStatusCell },
    { field: "date", headerName: "DATE", width: 120, minWidth: 110, renderCell: renderDateCell },
    { field: "term", headerName: "DURATION", width: 110, minWidth: 100, renderCell: renderTermCell },
    {
      field: "action",
      headerName: "ACTION",
      width: 110,
      minWidth: 100,
      renderCell: (params) => (
        <button
          type="button"
          onClick={() => handleRowClick(params.row)}
          className="action-btn action-btn-show shadow-xs"
          title="View Request Details"
        >
          <Eye size={12} />
          <span>View</span>
        </button>
      ),
    },
  ];

  return (
    <div className="inhouse-card">
      {/* Card Header */}
      <div className="inhouse-card-header" style={{ borderLeft: "4px solid #0284c7" }}>
        <div className="flex items-center gap-2">
          <span className="px-2.5 py-1 text-white font-semibold text-xs rounded-md bg-[#0284c7]">
            Customer Requests
          </span>
          <span className="text-slate-500 text-xs font-medium">
            Overview of logged customer calls, tokens, and resolution terms
          </span>
        </div>
        <span className="px-2.5 py-1 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-md shadow-xs">
          Total: {filteredRows.length}
        </span>
      </div>

      {/* Filter Toolbar Card */}
      <div className="p-3 bg-slate-50/70 border-b border-slate-200">
        <div className="grid grid-cols-12 gap-3 items-end">
          {/* Today Only Checkbox */}
          <div className="col-span-12 flex items-center justify-between pb-1 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="showTodayOnlyCheck"
                className="w-4 h-4 rounded text-[#133A69] focus:ring-blue-500/20"
                checked={showTodayOnly}
                onChange={() => setShowTodayOnly((prev) => !prev)}
              />
              <label htmlFor="showTodayOnlyCheck" className="text-xs font-bold text-slate-700 cursor-pointer select-none">
                Show Today Only
              </label>
            </div>
            <span className="text-xs text-slate-400 font-medium">Use filters to narrow requests</span>
          </div>

          {/* Token Name */}
          <div className="col-span-12 sm:col-span-6 md:col-span-3">
            <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1">
              Token / Call No.
            </label>
            <input
              type="text"
              name="tokenName"
              value={filters.tokenName}
              onChange={handleFilterChange}
              className="w-full px-3 py-1.5 text-xs bg-white border border-slate-300 rounded-lg focus:border-[#133A69] focus:ring-2 focus:ring-blue-500/20 outline-none transition-all placeholder:text-slate-400"
              placeholder="Filter by token..."
            />
          </div>

          {/* Company Name */}
          <div className="col-span-12 sm:col-span-6 md:col-span-3">
            <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1">
              Company Name
            </label>
            <Autocomplete
              options={companyNameOptions}
              value={filters.companyName}
              onChange={(event, newValue) =>
                setFilters((prev) => ({ ...prev, companyName: newValue || "" }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select company..."
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#ffffff",
                      borderRadius: "8px",
                      fontSize: "0.75rem",
                      "& fieldset": { borderColor: "#cbd5e1" },
                      "&:hover fieldset": { borderColor: "#94a3b8" },
                      "&.Mui-focused fieldset": { borderColor: "#133A69", borderWidth: "1.5px" },
                      "& input": { padding: "3.5px 8px !important" },
                    },
                  }}
                />
              )}
            />
          </div>

          {/* Project */}
          <div className="col-span-12 sm:col-span-6 md:col-span-2">
            <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1">
              Project
            </label>
            <Autocomplete
              options={projectOptions}
              value={filters.project}
              onChange={(event, newValue) =>
                setFilters((prev) => ({ ...prev, project: newValue || "" }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select project..."
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#ffffff",
                      borderRadius: "8px",
                      fontSize: "0.75rem",
                      "& fieldset": { borderColor: "#cbd5e1" },
                      "&:hover fieldset": { borderColor: "#94a3b8" },
                      "&.Mui-focused fieldset": { borderColor: "#133A69", borderWidth: "1.5px" },
                      "& input": { padding: "3.5px 8px !important" },
                    },
                  }}
                />
              )}
            />
          </div>

          {/* Support Team */}
          <div className="col-span-12 sm:col-span-6 md:col-span-2">
            <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1">
              Support Team
            </label>
            <Autocomplete
              options={supportTeamOptions}
              value={filters.supportTeam}
              onChange={(event, newValue) =>
                setFilters((prev) => ({ ...prev, supportTeam: newValue || "" }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select member..."
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#ffffff",
                      borderRadius: "8px",
                      fontSize: "0.75rem",
                      "& fieldset": { borderColor: "#cbd5e1" },
                      "&:hover fieldset": { borderColor: "#94a3b8" },
                      "&.Mui-focused fieldset": { borderColor: "#133A69", borderWidth: "1.5px" },
                      "& input": { padding: "3.5px 8px !important" },
                    },
                  }}
                />
              )}
            />
          </div>

          {/* Status */}
          <div className="col-span-12 sm:col-span-6 md:col-span-2">
            <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1">
              Status
            </label>
            <Autocomplete
              options={statusOptions}
              value={filters.status}
              onChange={(event, newValue) =>
                setFilters((prev) => ({ ...prev, status: newValue || "" }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select status..."
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#ffffff",
                      borderRadius: "8px",
                      fontSize: "0.75rem",
                      "& fieldset": { borderColor: "#cbd5e1" },
                      "&:hover fieldset": { borderColor: "#94a3b8" },
                      "&.Mui-focused fieldset": { borderColor: "#133A69", borderWidth: "1.5px" },
                      "& input": { padding: "3.5px 8px !important" },
                    },
                  }}
                />
              )}
            />
          </div>
        </div>
      </div>

      {/* Card Body with Table */}
      <div className="inhouse-card-body">
        <SupportStyleTable
          tableId="home_customer_requests"
          fileName="Customer_Requests_Summary"
          columns={columns}
          rows={filteredRows}
          onRowClick={handleRowClick}
          fullHeight={false}
          emptyComponent="No customer requests found matching criteria."
        />
      </div>
    </div>
  );
};

export default HomeShowAllCusReq;
